import React, { useCallback, ChangeEventHandler, memo } from 'react';
import { SubjectOption, SubjectOptionStates } from '@surveycake/logic-engine';
import { Typography } from '@surveycake/rc';
import { Icon } from '@surveycake/rc';
import { chevronDownOutlined } from '@surveycake/rc';
import { Wording } from '@surveycake/wording/survey';
import { useSurveyConfig } from 'app/core/survey-config';
import { addOptionPrefix } from 'app/modules/subject';
import { useStyles } from './styles';

export interface SelectProps {
  value: string;
  onChange: (selectedOption: string, checked: boolean) => void;
  options: SubjectOption[];
  optionStates: SubjectOptionStates;
}

function getPlaceholeer(
  value: string,
  options: SubjectOption[],
  wording: Wording
) {
  if (!value) {
    return wording.message.placeholder.select;
  }

  const selectedOption = options.find(
    ({ ID }) => addOptionPrefix(ID) === value
  );
  return selectedOption ? selectedOption.text : '';
}

function Select({ value, options, onChange, optionStates }: SelectProps) {
  const { wording } = useSurveyConfig();
  const handleChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
    event => onChange(event.target.value, true),
    [onChange]
  );
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <div className={styles.ghost}>
        <Typography
          className={styles.placeholder}
          variant="span"
          color="primary"
          noWrap
        >
          {getPlaceholeer(value, options, wording)}
        </Typography>
        <Icon className={styles.icon} icon={chevronDownOutlined} />
      </div>
      <select className={styles.select} value={value} onChange={handleChange}>
        <option value="">{wording.message.placeholder.select}</option>
        {options.map(({ ID, text }) => {
          const { visible, enable } = optionStates[ID];

          if (!visible) {
            return null;
          }

          const optionValue = addOptionPrefix(ID);
          const disabled = !enable;

          return (
            <option key={optionValue} value={optionValue} disabled={disabled}>
              {text}
              {disabled && ` - ${wording.message.error.option_fulled}`}
            </option>
          );
        })}
      </select>
    </div>
  );
}

export default memo(Select);
